<template>
  <component
    v-on="$listeners"
    :is="componentType"
    :to="mailto ? undefined : to"
    :href="href"
    :target="target"
    :class="{ button: true, primary, ghost, grayscale, [size]: true, wide, inline, loading }"
    :disabled="disabled"
    :type="isLink ? null : 'button'"
  >
    <transition name="loading">
      <loading-indicator-small :loaderAppearance="loaderAppearance" v-if="loading" />
    </transition>
    <template v-if="loading && loadingContext">
      {{ loadingContext }}
    </template>
    <slot v-else />
  </component>
</template>


<script>
  import { hasAllowedKeys } from '@sohohouse/common-frontend/utils'
  import LoadingIndicatorSmall from '~/components/global/loading-indicators/small'

  export default {
    props: {
      primary: {
        type: Boolean,
        default: false,
        required: false
      },
      ghost: {
        type: Boolean,
        default: false,
        required: false
      },
      grayscale: {
        type: Boolean,
        default: false,
        required: false
      },
      size: {
        type: String,
        required: false,
        default: 'medium',
        validator: value => ['xsmall', 'small', 'medium', 'large', 'xlarge'].indexOf(value) !== -1
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      loaderAppearance: {
        type: String,
        required: false,
        default: 'inline'
      },
      loadingContext: {
        type: String,
        required: false
      },
      to: {
        type: [String, Object],
        required: false,
        default: null,
        validator: value => {
          if (typeof value === 'string') return value !== ''
          if (!value.name) return false

          return hasAllowedKeys(value, ['name', 'params', 'query'])
        }
      },
      wide: {
        type: Boolean,
        required: false,
        default: false
      },
      inline: {
        type: Boolean,
        required: false,
        default: false
      },
      loading: {
        type: Boolean,
        required: false,
        default: false
      },
      target: {
        type: String,
        default: undefined
      }
    },
    computed: {
      mailto () {
        const type = typeof this.to === 'string'
        return (this.to && type) ? this.to.includes('mailto:') : false
      },
      external () {
        const type = typeof this.to === 'string'
        return (this.to && type) ? this.to.includes('://') : false
      },
      href () {
        const isLink = this.mailto || typeof this.to === 'string'
        return isLink ? this.to : undefined
      },
      componentType () {
        if (this.external) {
          return 'a'
        }
        if (this.to) {
          return 'nuxt-link'
        }

        return 'button'
      },
      isLink () {
        return this.to !== null
      }
    },
    components: {
      LoadingIndicatorSmall
    }

  }
</script>

<style lang="scss">

.button {
  cursor: pointer;
  display: inline-flex;
  position: relative;
}

.button {
  fill: $deprecated-f-color-highlight;
  padding: $d-xx-small $d-med-large;
  background: $deprecated-brand-pale-grey;
  border: 2px solid rgba($deprecated-f-color-highlight, 0.4);
  border-radius: 2px;
  width: 100%;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  @include label-x-sml;
  @include deprecated-f-color-highlight;

  @include breakpoint(medium) {
    width: auto;
  }

  &.loading {
    padding-left: calc(#{$d-med-large + $d-small/2});
    padding-right: calc(#{$d-med-large - $d-small/2});
  }

  // Sizes
  &.xsmall {
    @include label-xx-sml;
    padding: $d-tiny/2 $d-x-small;
    &.loading {
      padding-left: calc(#{$d-x-small + $d-x-small/2});
      padding-right: calc(#{$d-x-small - $d-x-small/2});
      .loading-animation-small {
        transform: translateX(-$d-x-small);
        height: 0.8em;
        width: 0.8em;
      }
    }
  }

  &.small {
    @include label-xx-sml;
    padding: $d-tiny $d-med-small;
    &.loading {
      padding-left: calc(#{$d-med-small + $d-x-small/2});
      padding-right: calc(#{$d-med-small - $d-x-small/2});
      .loading-animation-small {
        transform: translateX(-$d-x-small);
        height: 0.8em;
        width: 0.8em;
      }
    }
  }

  &.large {
    @include label-x-sml;
    min-width: $d-huge;
    padding: $d-x-small $d-med-large;
    &.loading {
      padding-left: calc(#{$d-med-large + $d-small/2});
      padding-right: calc(#{$d-med-large - $d-small/2});
    }
  }

  &.xlarge {
    @include label-sml;
    min-width: $d-huge * 1.5;
    padding: $d-x-small $d-large;
    &.loading {
      padding-left: calc(#{$d-large + $d-small/2});
      padding-right: calc(#{$d-large - $d-small/2});
    }
  }

  // Variations
  &:not(.ghost):not(.primary):not(.grayscale) {
    box-shadow: inset 0 0 0 rgba($deprecated-f-color-highlight, 1), inset 0 0 0 rgba($deprecated-f-color-highlight, 1),
      inset 0 0 0 rgba($deprecated-f-color-highlight, 1), inset 0 0 0 rgba($deprecated-f-color-highlight, 1);

    &:hover:not([disabled]) {
      background: $color-white;
      color: lighten($deprecated-f-color-highlight, 10);
      border-color: lighten($deprecated-f-color-highlight, 20);
      box-shadow: inset 1px 0 0 $color-black,
        inset -1px 0 0 $color-black,
        inset 0 -1px 0 $color-black,
        inset 0 1px 0 $color-black;
    }

    &:active:not([disabled]) {
      color: $color-white;
      background: $deprecated-f-color-highlight;
      border-color: $deprecated-f-color-highlight;
      box-shadow: inset 1px 0 0 $color-black,
        inset -1px 0 0 $color-black,
        inset 0 -1px 0 $color-black,
        inset 0 1px 0 $color-black;
    }
  }

  &.primary {
    background-color: $deprecated-brand-primary;
    border: none;
    color: $color-white;

    &:hover:not([disabled]) {
      background-color: $deprecated-brand-secondary;
    }

    &:active:not([disabled]) {
      background-color: darken($deprecated-brand-secondary, 10);
    }
  }

  &.ghost {
    background: transparent;
    border: none;
    text-transform: none;
    letter-spacing: normal;
  }

  &.grayscale {
    background-color: $deprecated-grey-x-med-light;
    color: $color-black;
    border: none;
    box-shadow: none;

    &:focus {
      &:after {
        opacity: 0;
        visibility: hidden;
      }
    }
  }

  &.wide {
    width: 100%;
  }

  &.inline {
    background: none;
    width: auto;
    margin-left: -$d-tiny;
    padding: $d-tiny;
    border: none;
    text-transform: none;
    letter-spacing: normal;
  }

  // DOM states
  &[disabled] {
    background: rgba($deprecated-grey-x-light, 0.8);
    border-color: $deprecated-grey-x-light;
    color: rgba($deprecated-grey-dark, 0.5);
    pointer-events: none;
    cursor: not-allowed;

    .icon {
      opacity: 0.5;
    }
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 3px;
    border: $line-focus;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;

    &.primary {
      top: -4px;
      left: -4px;
      right: -4px;
      bottom: -4px;
    }
  }

  &:focus {
    outline: none;
    box-shadow: none;
    &:after {
      opacity: 1;
      visibility: visible;
      transform: scale(1);
    }
    &:active {
      &:after {
        opacity: 0;
        visibility: hidden;
      }
    }
  }
}
</style>
